import { isAxiosError } from "axios";

import { HTTP_STATUS_TOO_MANY_REQUESTS } from "../constants/app";
import { MANUS_PAYLOAD_PLACEHOLDER } from "../constants/manus-automation-import";
import type { TAgentSelectionMap } from "../types/agent-import";
import { EAutomationType } from "../types/automation";
import {
  EManusAutomationUnsupportedReason,
  EManusAutomationWarning,
  type IBuildManusAutomationSelectionsProps,
  type IImportManusAutomationRequest,
  type IManusAutomationPreview,
  type TManusAutomationAgentTypeKey,
  type TManusAutomationNoticeKey,
  type TManusAutomationNoticeParams,
} from "../types/manus-automation-import";
import { buildAgentSelections } from "./agent-import-selections";

const WARNING_KEYS = {
  [EManusAutomationWarning.SCRIPT]: "selectAutomations.warnings.script",
  [EManusAutomationWarning.PAYLOAD]: "selectAutomations.warnings.payload",
  [EManusAutomationWarning.CONNECTORS]: "selectAutomations.warnings.connectors",
  [EManusAutomationWarning.EXTRA_TRIGGERS]:
    "selectAutomations.warnings.extraTriggers",
  [EManusAutomationWarning.WEBHOOK_URL]:
    "selectAutomations.warnings.webhookUrl",
  [EManusAutomationWarning.WEBHOOK_FILTERS]:
    "selectAutomations.warnings.webhookFilters",
  [EManusAutomationWarning.EXPIRY]: "selectAutomations.warnings.expiry",
} as const satisfies Record<EManusAutomationWarning, TManusAutomationNoticeKey>;

const UNSUPPORTED_KEYS = {
  [EManusAutomationUnsupportedReason.EVENT_SOURCE]:
    "selectAutomations.unsupported.eventSource",
  [EManusAutomationUnsupportedReason.SCHEDULE]:
    "selectAutomations.unsupported.schedule",
  [EManusAutomationUnsupportedReason.ONE_TIME_PASSED]:
    "selectAutomations.unsupported.oneTimePassed",
  [EManusAutomationUnsupportedReason.NO_TRIGGER]:
    "selectAutomations.unsupported.noTrigger",
  [EManusAutomationUnsupportedReason.NO_INSTRUCTION]:
    "selectAutomations.unsupported.noInstruction",
} as const satisfies Record<
  EManusAutomationUnsupportedReason,
  TManusAutomationNoticeKey
>;

// NOTE: NSAI-8009 — the selection-step rules web and mobile share for the
// Manus automation import.

export const isManusAutomationImportable = (
  automation: IManusAutomationPreview,
): boolean => automation.agentType !== null;

// NOTE: Only importable automations get a selection, so an unsupported one can
// never be ticked or sent; each starts ticked with the default model, as in the
// assistant imports.
export const buildManusAutomationSelections = ({
  automations,
  defaultModelUuid,
}: IBuildManusAutomationSelectionsProps): TAgentSelectionMap =>
  buildAgentSelections({
    agentIds: automations
      .filter(isManusAutomationImportable)
      .map((automation) => automation.id),
    defaultModelUuid,
  });

export const toManusAutomationImportRequests = (
  selections: TAgentSelectionMap,
): IImportManusAutomationRequest[] =>
  Object.entries(selections)
    .filter(([, selection]) => selection.isSelected)
    .map(([automationId, selection]) => ({
      automationId,
      modelUuid: selection.modelUuid,
    }));

// NOTE: What the user must finish by hand, in the order the server lists it.
export const getManusAutomationWarningKeys = (
  automation: IManusAutomationPreview,
): TManusAutomationNoticeKey[] =>
  automation.warnings.map((warning) => WARNING_KEYS[warning]);

export const getManusAutomationUnsupportedKey = (
  automation: IManusAutomationPreview,
): TManusAutomationNoticeKey | null =>
  automation.unsupportedReason === null
    ? null
    : UNSUPPORTED_KEYS[automation.unsupportedReason];

export const buildManusAutomationNoticeParams = (
  automation: IManusAutomationPreview,
): TManusAutomationNoticeParams => ({
  placeholder: MANUS_PAYLOAD_PLACEHOLDER,
  names: automation.connectorNames.join(", "),
  event: automation.unsupportedEvent ?? "",
});

export const getManusAutomationAgentTypeKey = (
  agentType: EAutomationType | null | undefined,
): TManusAutomationAgentTypeKey => {
  if (agentType === EAutomationType.CRON_JOB) {
    return "selectAutomations.agentType.scheduledTask";
  }
  if (agentType === EAutomationType.WEBHOOK) {
    return "selectAutomations.agentType.webhook";
  }
  return "selectAutomations.agentType.unsupported";
};

// NOTE: The server answers a Manus hiccup with 502 and a dead export (a failed
// task, an unusable reply, a rejected key) with 400. A hiccup, no answer at
// all, or NodeShift's own rate limit (429 — two tabs polling one account) may
// pass on a retry of the same export; anything else needs a new one.
export const isTransientManusExportError = (error: unknown): boolean =>
  isAxiosError(error) &&
  (error.response === undefined ||
    error.response.status >= 500 ||
    error.response.status === HTTP_STATUS_TOO_MANY_REQUESTS);
